import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

export const AFFILIATE_LEVELS = [
  { level: 1, revenue: 50, turnover: 2.0, minDeposits: 0, maxDeposits: 14 },
  { level: 2, revenue: 55, turnover: 2.5, minDeposits: 15, maxDeposits: 49 },
  { level: 3, revenue: 60, turnover: 3.0, minDeposits: 50, maxDeposits: 99 },
  { level: 4, revenue: 65, turnover: 3.5, minDeposits: 100, maxDeposits: 199 },
  { level: 5, revenue: 70, turnover: 4.0, minDeposits: 200, maxDeposits: 399 },
  { level: 6, revenue: 75, turnover: 4.5, minDeposits: 400, maxDeposits: 699 },
  { level: 7, revenue: 80, turnover: 5.0, minDeposits: 700, maxDeposits: Number.POSITIVE_INFINITY },
];

export function normalizeProgram(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (raw.includes("revenue")) return "revenue";
  if (raw.includes("turnover") || raw.includes("volume")) return "turnover";
  return "turnover";
}

export function formatCurrency(value) {
  return `$${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function toDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function roundCurrency(value) {
  return Number(Number(value || 0).toFixed(2));
}

function monthKey(date) {
  const safeDate = toDate(date);
  if (!safeDate) return null;
  return `${safeDate.getFullYear()}-${String(safeDate.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(date) {
  return date.toLocaleString(undefined, {
    month: "short",
    year: "2-digit",
  });
}

function isApprovedDeposit(deposit) {
  const status = String(deposit?.status || "").trim().toLowerCase();
  if (!status) return true;
  return ["approved", "success", "successful", "completed", "paid"].includes(status);
}

function chunk(array, size) {
  const out = [];
  for (let index = 0; index < array.length; index += size) {
    out.push(array.slice(index, index + size));
  }
  return out;
}

async function fetchAffiliateProfile(userId) {
  const [affiliateSnap, userSnap] = await Promise.all([
    getDoc(doc(db, "affiliate_users", userId)),
    getDoc(doc(db, "users", userId)),
  ]);

  if (affiliateSnap.exists() && userSnap.exists()) {
    return {
      id: userId,
      ...userSnap.data(),
      ...affiliateSnap.data(),
    };
  }
  if (affiliateSnap.exists()) {
    return { id: userId, ...affiliateSnap.data() };
  }
  if (userSnap.exists()) {
    return { id: userId, ...userSnap.data() };
  }
  return null;
}

async function fetchAffiliateLinks(userId) {
  const snap = await getDocs(query(collection(db, "affiliate_links"), where("userId", "==", userId)));
  return snap.docs
    .map((entry) => ({ id: entry.id, ...entry.data() }))
    .sort((left, right) => {
      const rightTime = toDate(right.createdAt)?.getTime() || 0;
      const leftTime = toDate(left.createdAt)?.getTime() || 0;
      return rightTime - leftTime;
    });
}

async function fetchReferredUsers(userId, linkIds) {
  const users = new Map();

  const directSnap = await getDocs(query(collection(db, "users"), where("affiliateId", "==", userId)));
  directSnap.forEach((entry) => {
    const data = entry.data();
    if (!linkIds.length || !data.affiliateLinkId || linkIds.includes(data.affiliateLinkId)) {
      users.set(entry.id, { id: entry.id, ...data });
    }
  });

  for (const batch of chunk(linkIds, 10)) {
    if (!batch.length) continue;
    const linkSnap = await getDocs(query(collection(db, "users"), where("affiliateLinkId", "in", batch)));
    linkSnap.forEach((entry) => {
      users.set(entry.id, { id: entry.id, ...entry.data() });
    });
  }

  return [...users.values()];
}

async function fetchDeposits(userIds) {
  const allDeposits = [];
  for (const batch of chunk(userIds, 10)) {
    if (!batch.length) continue;
    const snap = await getDocs(query(collection(db, "deposits"), where("userId", "in", batch)));
    snap.forEach((entry) => {
      allDeposits.push({ id: entry.id, ...entry.data() });
    });
  }
  return allDeposits.filter(isApprovedDeposit);
}

async function fetchAffiliateCommissions(userId) {
  const snap = await getDocs(
    query(collection(db, "affiliate_commissions"), where("affiliateUserId", "==", userId)),
  );
  return snap.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
}

function getAffiliateLevel(depositCount) {
  return (
    AFFILIATE_LEVELS.find(
      (level) => depositCount >= level.minDeposits && depositCount <= level.maxDeposits,
    ) || AFFILIATE_LEVELS[0]
  );
}

function buildTimeline(referredUsers, deposits, firstTimeDeposits, links, commissions) {
  const monthStarts = [];
  const today = new Date();

  for (let offset = 5; offset >= 0; offset -= 1) {
    monthStarts.push(new Date(today.getFullYear(), today.getMonth() - offset, 1));
  }

  const timeline = monthStarts.map((date) => ({
    key: monthKey(date),
    date: monthLabel(date),
    clicks: 0,
    registrations: 0,
    ftd: 0,
    deposits: 0,
    commission: 0,
  }));

  const timelineMap = new Map(timeline.map((entry) => [entry.key, entry]));

  referredUsers.forEach((user) => {
    const key = monthKey(user.createdAt);
    if (key && timelineMap.has(key)) {
      timelineMap.get(key).registrations += 1;
    }
  });

  deposits.forEach((deposit) => {
    const key = monthKey(deposit.createdAt || deposit.timestamp);
    if (key && timelineMap.has(key)) {
      timelineMap.get(key).deposits += Number(deposit.amount || 0);
    }
  });

  firstTimeDeposits.forEach((deposit) => {
    const key = monthKey(deposit.createdAt || deposit.timestamp);
    if (key && timelineMap.has(key)) {
      timelineMap.get(key).ftd += 1;
    }
  });

  commissions.forEach((commission) => {
    const key = monthKey(commission.createdAt);
    if (key && timelineMap.has(key)) {
      timelineMap.get(key).commission += Number(commission.commissionAmount || 0);
    }
  });

  const clickTotal = links.reduce(
    (sum, link) => sum + Number(link.clicks || link.totalClicks || link.visits || 0),
    0,
  );

  if (timeline.length) {
    timeline[timeline.length - 1].clicks = clickTotal;
  }

  return timeline;
}

export async function buildAffiliateAnalytics(userId) {
  const [profile, links] = await Promise.all([
    fetchAffiliateProfile(userId),
    fetchAffiliateLinks(userId),
  ]);

  const linkIds = links.map((link) => link.id);
  const linksById = new Map(links.map((link) => [link.id, link]));
  const referredUsers = await fetchReferredUsers(userId, linkIds);
  const [deposits, commissions] = await Promise.all([
    fetchDeposits(referredUsers.map((user) => user.id)),
    fetchAffiliateCommissions(userId),
  ]);

  const depositsByUser = new Map();
  deposits.forEach((deposit) => {
    if (!depositsByUser.has(deposit.userId)) {
      depositsByUser.set(deposit.userId, []);
    }
    depositsByUser.get(deposit.userId).push(deposit);
  });

  const commissionsByUser = new Map();
  commissions.forEach((commission) => {
    const referredUserId = commission.referredUserId;
    if (!referredUserId) return;
    if (!commissionsByUser.has(referredUserId)) {
      commissionsByUser.set(referredUserId, []);
    }
    commissionsByUser.get(referredUserId).push(commission);
  });

  depositsByUser.forEach((items) => {
    items.sort((left, right) => {
      const leftTime = toDate(left.createdAt || left.timestamp)?.getTime() || 0;
      const rightTime = toDate(right.createdAt || right.timestamp)?.getTime() || 0;
      return leftTime - rightTime;
    });
  });

  const totalDepositsCount = deposits.length;
  const totalDepositsSum = deposits.reduce((sum, deposit) => sum + Number(deposit.amount || 0), 0);
  const firstTimeDeposits = [...depositsByUser.values()].map((items) => items[0]).filter(Boolean);
  const ftdsCount = firstTimeDeposits.length;
  const ftdsSum = firstTimeDeposits.reduce((sum, deposit) => sum + Number(deposit.amount || 0), 0);
  const level = getAffiliateLevel(totalDepositsCount);

  const rows = referredUsers
    .map((user) => {
      const userDeposits = depositsByUser.get(user.id) || [];
      const userCommissions = commissionsByUser.get(user.id) || [];
      const depositsSum = userDeposits.reduce((sum, deposit) => sum + Number(deposit.amount || 0), 0);
      const commissionSum = userCommissions.reduce(
        (sum, item) => sum + Number(item.commissionAmount || 0),
        0,
      );
      const link = linksById.get(user.affiliateLinkId) || null;
      const program = normalizeProgram(
        user.affiliateProgramType || user.affiliateProgram || link?.program,
      );
      const turnoverAmount = Number(user.turnoverCompleted ?? user.turnover ?? depositsSum);
      const createdAt = toDate(user.createdAt);

      return {
        id: user.id,
        trader: user.name || user.username || user.email || `#${user.id.slice(-6)}`,
        traderMeta: user.email || user.id,
        linkId: user.affiliateLinkId || "-",
        linkComment: link?.comment || "",
        program,
        deposits: userDeposits.length,
        depositsSum,
        turnover: turnoverAmount,
        commission: roundCurrency(commissionSum),
        createdAt,
      };
    })
    .sort((left, right) => {
      const rightTime = right.createdAt?.getTime() || 0;
      const leftTime = left.createdAt?.getTime() || 0;
      return rightTime - leftTime;
    });

  const totalTurnover = rows.reduce((sum, row) => sum + row.turnover, 0);
  const totalCommissionFromRows = rows.reduce((sum, row) => sum + row.commission, 0);
  const unlockedBonus = roundCurrency(profile?.affiliateBonusUnlocked || 0);
  const lockedBonus = roundCurrency(profile?.affiliateBonusLocked || 0);
  const pendingWithdrawals = roundCurrency(profile?.affiliatePendingWithdrawals || 0);
  const totalCommission = roundCurrency(
    profile?.affiliateTotalEarned ?? totalCommissionFromRows,
  );

  return {
    profile,
    links,
    referredUsers,
    rows,
    commissions,
    level,
    timeline: buildTimeline(referredUsers, deposits, firstTimeDeposits, links, commissions),
    totals: {
      balance: unlockedBonus,
      unlockedBonus,
      lockedBonus,
      pendingWithdrawals,
      earnings: totalCommission,
      registrations: referredUsers.length,
      clicks: links.reduce(
        (sum, link) => sum + Number(link.clicks || link.totalClicks || link.visits || 0),
        0,
      ),
      depositsCount: totalDepositsCount,
      depositsSum: totalDepositsSum,
      ftdsCount,
      ftdsSum,
      totalTurnover: roundCurrency(totalTurnover),
      totalCommission,
      totalWithdrawn: roundCurrency(profile?.affiliateTotalWithdrawn || 0),
      turnoverRate: level.turnover / 100,
      revenueRate: level.revenue / 100,
    },
  };
}
